import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { Container, Row, Col } from "react-bootstrap";
import { SvgIcon, useTheme } from '@mui/material';
import BackNext from '../../Common/SubComponents/BackNext';
import { StyledRadioInput, TextInput, SelectInput, DateInput } from "../../../Helpers/Inputs/index";
import Hand from '../icons/Hand.svg?react';
import GetStateListForAflac from '../Helpers/StateListForAflac';
import { object, string, array as yuparray, mixed, customValidators } from '../../../Helpers/Validators/customValidators';
import { ARIZONA, MARYLAND, COLORADO, OREGON, MAINE } from '../../../Helpers/Constants/States';
import IneligibleForm from '../Helpers/Input/IneligibleForm';
import { setNestedValue } from '../Helpers/Constant';
import Warning from '../icons/warning.svg?react';
import './HIVEligibility.scss';

export default function HIVEligibility(props) {

    const theme = useTheme();
    const [dateCalenderOpen, setDateCalenderOpen] = useState(false);
    const { state } = props.basicInfo;

    const formMethods = useForm({
        resolver: yupResolver(HIVEligibility.ValidationSchema),
        mode: 'all',
        reValidateMode: 'all',
        defaultValues: props.formState
    });

    const { handleSubmit, trigger, register, setValue } = formMethods;
    const ineligibleDefault = [
        {
            type: '',
            name: [],
            numberOfChildren: '',
            nameofChildren: [],
        },
    ];
    useEffect(() => {
        props.onFieldChange("isVisible", true);
        if (state === COLORADO && props.formState.hiv === 'Yes' && props.formState.ineligible.length === 0) {
            handleValueChangeWithIneligible('hiv', 'Yes', true)
        }
    }, [])

    useEffect(() => {
        register('state');
        setValue('state', state);
    }, [register])

    const handleValueChangeWithIneligible = (field, value, isInElegible = false) => {
        if (!isInElegible) {
            handleValueChange(field, value);
            return;
        }
        if (value === 'Yes') {
            const newForm = ineligibleDefault;
            newForm[0].type = field;
            const ineligibleForm = [...props.formState.ineligible, newForm[0]];
            props.onStepFieldChange(field, value, 'ineligible', ineligibleForm);
            setValue('ineligible', ineligibleForm);
            trigger(field);
        } else {
            const ineligibleForm = props.formState.ineligible.filter((form, formIndex) => form.type !== field);
            props.onStepFieldChange(
                field,
                value,
                'ineligible',
                ineligibleForm
            );
            setValue('ineligible', ineligibleForm);
        }
    };

    const handleValueChange = (field, value) => {
        if (field === 'hivPositive' && value === "No" && state === ARIZONA) {
            const form = { ...props.fullFormState };
            form.steps.hivEligibility.hivTestsPerformed = '';
            form.steps.hivEligibility.hivPhysicianCity = '';
            form.steps.hivEligibility.hivPhysicianName = '';
            form.steps.hivEligibility.hivPhysicianStreet = '';
            form.steps.hivEligibility.hivPhysicianState = '';
            form.steps.hivEligibility.hivPhysicianZip = '';
            form.steps.hivEligibility.hivTestsDate = null;
            setValue('hivTestsDate', null);
            form.steps.hivEligibility.hivPositive = value;
            props.onMultiFieldChange(form);
        }
        else {
            props.onFieldChange(field, value);
        }
        trigger(field);
    };

    const getIneligibleNames = () => {
        return (
            [
                { value: "Name of Proposed Insured", label: "Name of Proposed Insured" },
                { value: "Name of Spouse", label: "Name of Spouse" },
                { value: "Dependent Child", label: "Dependent Child" },
            ]
        )
    }

    const getIneligibleForCoverage = () => {
        return (
            [
                { value: "Proposed Insured", label: "Proposed Insured" },
                { value: "Spouse", label: "Spouse" },
                { value: "Child", label: "Child" },
            ]
        )
    }

    const getImportantNoticeText = () => {
        const formState = props.formState;
        let noticeText = 'To evaluate your insurability, Aflac has requested that you provide a sample of your blood for testing and analysis to determine the presence of human immunodeficiency virus (HIV) antibodies.';
        if ((displayWarning && state === ARIZONA && formState.hiv === 'Yes' && formState.hivPositive === 'Yes') || (state === COLORADO && formState.hiv === 'Yes' && formState.hivPositive === 'No' && formState.hivPositiveMultispot === 'Yes')) {
            noticeText = 'Because you were unable to provide the required information regarding your HIV testing, to evaluate your insurability, Aflac has requested that you provide a sample of your blood for testing and analysis to determine the presence of human immunodeficiency virus (HIV) antibodies.';
        }

        return noticeText;
    }

    const getAidsLabel = () => {
        if (state === MARYLAND) {
            return 'Within the last five years, has anyone to be covered been diagnosed with or treated for known symptoms or known indications of acquired immune deficiency syndrome (AIDS) by a member of the medical profession, or tested positive for human immunodeficiency virus (HIV)?';
        } else if (state === MAINE) {
            return 'Within the last five years, has anyone to be covered been diagnosed with or treated for acquired immune deficiency syndrome (AIDS) by a member of the medical profession, or has anyone to be covered tested positive for human immunodeficiency virus (HIV)?';
        } else {
            return 'Within the last five years, has anyone to be covered been diagnosed with or treated for acquired immune deficiency syndrome (AIDS) by a member of the medical profession, or has anyone to be covered tested positive for human immunodeficiency virus (HIV)?';
        }
    };

    const onSubmit = () => {
        props.onNext();
    };

    const showInEligible = ((props.formState.hiv === 'Yes' && state === COLORADO) || (props.formState.aids === 'Yes' && state === MAINE));
    const inEligibleType = showInEligible ? props.formState.hiv === 'Yes' ? 'hiv' : 'aids' : '';
    const displayWarning = (!props.formState.hivTestsPerformed || !props.formState.hivPhysicianName || !props.formState.hivPhysicianStreet || !props.formState.hivPhysicianCity || !props.formState.hivPhysicianState || !props.formState.hivPhysicianZip || !props.formState.hivTestsDate);

    const getIneligibleComponent = (field, list) => {
        let item;
        list.map((ineligible, index) => {
            if (ineligible.type === field) {
                item = ineligible;
                item.index = index;
            }
        });
        const handleValueChangeloop = (field, type, value, longFieldName) => {
            setValue(longFieldName, value);
            props.onFieldChange(
                'ineligible',
                props.formState.ineligible.map((form, formIndex) => {
                    if (type === form.type) {
                        if (field === "name")
                            form[field] = value;
                        else
                            setNestedValue(form, field, value);
                        return form;
                    } else {
                        return form;
                    }
                })
            );
        };
        const handleChildrenNames = (field, type, value, longFieldName) => {
            const resetIneligible = props.formState.ineligible.map((form, formIndex) => {
                if (type === form.type) {
                    if (field === 'numberOfChildren') {
                        setNestedValue(form, field, value);
                        const previousChildLength = form.nameofChildren.length;
                        const totalChild = parseInt(value);
                        if (totalChild > previousChildLength) {
                            const addChildInput = totalChild - previousChildLength;
                            for (let i = 1; i <= addChildInput; i++) {
                                if (type === form.type) {
                                    form.nameofChildren.push({ name: '' });
                                }
                            }
                        } else {
                            const removeChildInput = previousChildLength - totalChild;
                            form.nameofChildren.splice(`-${removeChildInput}`);
                        }
                        return form;
                    }
                } else {
                    if (type === form.type) {
                        setNestedValue(form, field, value);
                        return form;
                    } else {
                        return form;
                    }
                }
            })
            props.onFieldChange('ineligible', resetIneligible);
            setValue('ineligible', resetIneligible);
        };

        return (
            <div>
                {item &&
                    <IneligibleForm
                        formState={item}
                        onChange={(field, value, type, longFieldName) => {
                            handleValueChangeloop(field, type, value, longFieldName);
                        }}
                        ChildChange={(field, value, type, longFieldName) => {
                            handleChildrenNames(field, type, value, longFieldName);
                        }}
                        allProps={props}
                        trigger={trigger}
                        key={item.index}
                        index={item.index}
                        customOption={props.getListOfPlanIncludedPerson()}
                        noOfChilds={props.noOfChilds ? props.noOfChilds : ''}
                    />
                }
            </div>
        );
    };
    return (
        <Container className="border-class" fluid>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Row className="justify-content-center mt-4">
                        <Col lg={5}>
                            <div className="text-center mb-2 pt-4">
                                <SvgIcon
                                    sx={{
                                        '& path': {
                                            stroke: theme.palette.primary.main, // override stroke directly
                                        },
                                    }}
                                    component={Hand}
                                    className="small-icons"
                                    fontSize="large"
                                />
                            </div>
                            <div className="text-center mb-4 form-title">HIV Eligibility</div>
                        </Col>
                    </Row>
                    <Row className="hiv-eligibility justify-content-center">
                        <Col className='col-div'>
                            {
                                state === ARIZONA &&
                                (<div className='mb-3'>
                                    <h6 className='pb-2'>Has anyone to be covered ever tested positive for human immunodeficiency virus (HIV)?</h6>
                                    <StyledRadioInput
                                        name="hiv"
                                        id="hiv"
                                        value={props.formState.hiv}
                                        options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                        onChange={(e) => handleValueChangeWithIneligible('hiv', e)}
                                        isWhiteBackground={true}
                                        isBlankIcon={true}
                                    />
                                </div>)
                            }
                            {
                                state === COLORADO &&
                                <div className='mb-3'>
                                    <h6 className='pb-2'>Has anyone to be covered ever tested positive for the human immunodeficiency virus (HIV) or HTLV-III (antibodies or human T-lymphotropic virus Type II)?</h6>
                                    <StyledRadioInput
                                        name="hiv"
                                        id="hiv"
                                        value={props.formState.hiv}
                                        options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                        onChange={(e) => handleValueChangeWithIneligible('hiv', e, true)}
                                        isWhiteBackground={true}
                                        isBlankIcon={true}
                                    />
                                </div>
                            }
                            {
                                (state === MARYLAND || state === MAINE || state === OREGON) &&
                                <React.Fragment>
                                    {state === MAINE && (
                                        <div className="hiv-quote">
                                            *ANSWER THIS QUESTION “NO” IF YOU HAVE TESTED POSITIVE FOR HIV AND HAVE NOT DEVELOPED SYMPTOMS OF THE DISEASE AIDS.*
                                        </div>
                                    )}
                                    <div className='mb-3'>
                                        <h6>{getAidsLabel()}</h6>
                                        <StyledRadioInput
                                            name="aids"
                                            id="aids"
                                            value={props.formState.aids}
                                            options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                            onChange={(e) => handleValueChangeWithIneligible('aids', e, state === MAINE)}
                                            isWhiteBackground={true}
                                            isBlankIcon={true}
                                        />
                                    </div>
                                </React.Fragment>
                            }
                            {showInEligible && getIneligibleComponent(inEligibleType, props.formState.ineligible)}

                            {state === ARIZONA && props.formState.hiv === 'Yes' &&
                                <div className='mb-3'>
                                    <h6 className='pb-2'>Have you had tests confirming the positive HIV test result or diagnosis such as a second positive screening test (the ELISA test) and a positive supplemental test (the Western Blot test)?</h6>
                                    <StyledRadioInput
                                        name="hivPositive"
                                        id="hivPositive"
                                        value={props.formState.hivPositive}
                                        options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                        onChange={(e) => handleValueChange('hivPositive', e)}
                                        isWhiteBackground={true}
                                        isBlankIcon={true}
                                    />
                                </div>
                            }

                            {((props.formState.hiv === 'Yes' && state === COLORADO) || ((state === MARYLAND || state === OREGON) && props.formState.aids === "Yes")) &&
                                <div className='mb-3'>
                                    <h6 className='pb-2'>{`Has the result been substantiated by two ELISA or EIA tests and one Western Blot Blood Test${state !== OREGON ? ' or one HIV 1/2 Multispot test' : ''}?`}</h6>
                                    <StyledRadioInput
                                        name="hivSubstantiated"
                                        id="hivSubstantiated"
                                        value={props.formState.hivSubstantiated}
                                        options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                        onChange={e => handleValueChange('hivSubstantiated', e)}
                                        isWhiteBackground={true}
                                        isBlankIcon={true}
                                    />
                                </div>
                            }

                            {state === COLORADO && props.formState.hivSubstantiated === 'No' &&
                                <div className='mb-3'>
                                    <h6 className='pb-2'>Have you had tests confirming the positive HIV test result or diagnosis such as a second positive screening test (the ELISA test) and a positive supplemental test (the Western Blot test or HIV 1/2 Multispot test)?</h6>
                                    <StyledRadioInput
                                        name="hivPositiveMultispot"
                                        id="hivPositiveMultispot"
                                        value={props.formState.hivPositiveMultispot}
                                        options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                        onChange={e => handleValueChange('hivPositiveMultispot', e)}
                                        isWhiteBackground={true}
                                        isBlankIcon={true}
                                    />
                                </div>
                            }

                            {state === MARYLAND && props.formState.hivSubstantiated === 'Yes' &&
                                <div className='mb-3'>
                                    <h6 className='pb-2'>Have you had a positive test result substantiated by two ELISA tests and a Western Blot test in the past seven years?</h6>
                                    <StyledRadioInput
                                        name="hivSubstantiatedSevenYears"
                                        id="hivSubstantiatedSevenYears"
                                        value={props.formState.hivSubstantiatedSevenYears}
                                        options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                        onChange={e => handleValueChange('hivSubstantiatedSevenYears', e)}
                                        isWhiteBackground={true}
                                        isBlankIcon={true}
                                    />
                                </div>
                            }

                            {(props.formState.hiv === "Yes" && (props.formState.hivSubstantiated === "Yes" && state !== COLORADO)) &&
                                <Fragment>
                                    <div className='mb-3' hidden>
                                        <h6 className='pb-2'>If any one of Questions 1 through 8 is answered yes, was it the:</h6>
                                        <SelectInput
                                            name="ineligibleForCoverage"
                                            id="ineligibleForCoverage"
                                            value={props.formState.ineligibleForCoverage}
                                            onChange={e => handleValueChange('ineligibleForCoverage', e.target.value)}
                                            label="If Yes, Who:"
                                            selectOptions={getIneligibleForCoverage()}
                                        />
                                    </div>
                                    <div className='mb-3' hidden>
                                        <h6 className='pb-2'>Any person(s) indicated above will not be covered under the policy and/or rider(s).  If the named person is the Proposed Insured, a policy and/or rider(s) will not be issued; therefore, do not submit this application.</h6>
                                        <SelectInput
                                            name="ineligibleNames"
                                            id="ineligibleNames"
                                            value={props.formState.ineligibleNames}
                                            onChange={e => handleValueChange('ineligibleNames', e.target.value)}
                                            // label="If Yes, Who:"
                                            selectOptions={getIneligibleNames()}
                                        />
                                    </div>
                                </Fragment>
                            }
                            <div className='mb-3' hidden>
                                <h6 className='pb-2'>If a child, are any other children to be covered?</h6>
                                <StyledRadioInput
                                    name="anotherChild"
                                    id="anotherChild"
                                    value={props.formState.anotherChild}
                                    options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                    onChange={e => handleValueChange('anotherChild', e)}
                                    isWhiteBackground={true}
                                    isBlankIcon={true}
                                />
                            </div>
                            {((props.formState.hiv === "Yes" && ((props.formState.hivPositive === "Yes" && state === ARIZONA) || (props.formState.hivSubstantiated === "Yes" && state !== COLORADO) || (props.formState.hivSubstantiated === "No" && props.formState.hivPositiveMultispot === 'Yes' && state === COLORADO))) || (props.formState.aids === 'Yes' && (state === MARYLAND && props.formState.hivSubstantiated === 'Yes' && props.formState.hivSubstantiatedSevenYears === 'Yes') || (state === OREGON && props.formState.hivSubstantiated === 'Yes'))) &&
                                <Fragment>
                                    <div className='mb-3'>
                                        <TextInput
                                            name="hivTestsPerformed"
                                            id="hivTestsPerformed"
                                            label="What confirmatory tests were performed?"
                                            value={props.formState.hivTestsPerformed}
                                            onChange={(e) => handleValueChange('hivTestsPerformed', e.target.value)}
                                            multiline
                                            rows={6}
                                        />
                                    </div>
                                    <div className='mb-3'>
                                        <h6 className='pb-2'>Please provide the name and address of the doctor or laboratory that performed the tests.</h6>
                                        <h6 className='pb-2'>Physician</h6>
                                        <TextInput
                                            name="hivPhysicianName"
                                            id="hivPhysicianName"
                                            label="Name"
                                            value={props.formState.hivPhysicianName}
                                            onChange={(e) => handleValueChange('hivPhysicianName', e.target.value)}
                                        />
                                    </div>
                                    <div className='mb-3'>
                                        <TextInput
                                            name="hivPhysicianStreet"
                                            id="hivPhysicianStreet"
                                            label="Street Address"
                                            value={props.formState.hivPhysicianStreet}
                                            onChange={(e) => handleValueChange('hivPhysicianStreet', e.target.value)}
                                        />
                                    </div>
                                    <div className='mb-3'>
                                        <TextInput
                                            name="hivPhysicianCity"
                                            id="hivPhysicianCity"
                                            label="City"
                                            value={props.formState.hivPhysicianCity}
                                            onChange={(e) => handleValueChange('hivPhysicianCity', e.target.value)}
                                        />
                                    </div>
                                    <div className='mb-3'>
                                        <SelectInput
                                            name="hivPhysicianState"
                                            id="hivPhysicianState"
                                            value={props.formState.hivPhysicianState}
                                            onChange={e => handleValueChange('hivPhysicianState', e.target.value)}
                                            label="State"
                                            selectOptions={GetStateListForAflac()}
                                        />
                                    </div>
                                    <div className='mb-3'>
                                        <TextInput
                                            name="hivPhysicianZip"
                                            id="hivPhysicianZip"
                                            label="Zip"
                                            value={props.formState.hivPhysicianZip}
                                            onChange={(e) => handleValueChange('hivPhysicianZip', e.target.value)}
                                        />
                                    </div>
                                    <div className='mb-3'>
                                        <h6 className='pb-2'>When were these tests performed?</h6>
                                        <DateInput
                                            calenderOpen={dateCalenderOpen}
                                            setCalenderOpen={setDateCalenderOpen}
                                            name="hivTestsDate"
                                            id="hivTestsDate"
                                            label="Date tests were performed."
                                            format="MMMM D, YYYY"
                                            value={props.formState.hivTestsDate ? dayjs(props.formState.hivTestsDate) : null}
                                            maxDate={dayjs()}
                                            onChange={value => handleValueChange('hivTestsDate', value)}
                                            fullWidth
                                            InputProps={{ readOnly: true }}
                                        />
                                    </div>
                                </Fragment>
                            }
                            {
                                (((props.formState.hivPositive && state === ARIZONA) && props.formState.hiv === "Yes") || ((props.formState.hivSubstantiated === "No" && state !== COLORADO) && props.formState.hiv === "Yes") || ((state === OREGON || state === MARYLAND) && props.formState.aids === "Yes" && ((props.formState.hivSubstantiated === "No" && state === OREGON) || (state === MARYLAND && props.formState.hivSubstantiated === "Yes" && props.formState.hivSubstantiatedSevenYears === 'No'))) || (state === MAINE && props.formState.aids === "No") || (state === COLORADO && props.formState.hivSubstantiated === 'No' && props.formState.hivPositiveMultispot === 'No')) &&
                                <Fragment>
                                    {(!displayWarning && state === ARIZONA) ? null :
                                        <Row>
                                            <p className='tip-box mx-3'>
                                                <b className='inside-title'>
                                                    <SvgIcon component={Warning} fontSize="large" className='inside-icon' />
                                                    Important:{' '}
                                                </b>
                                                <b className='inside-text'>{getImportantNoticeText()}</b>
                                            </p>
                                        </Row>
                                    }
                                    <div className='mb-3'>
                                        <h6 className='pb-2'>Would you like to designate a Physician for receipt of your test results if positive?</h6>
                                        <StyledRadioInput
                                            name="hivReceiptPhysician"
                                            id="hivReceiptPhysician"
                                            value={props.formState.hivReceiptPhysician}
                                            options={[{ value: "Yes", label: "Yes" }, { value: "No", label: "No" }]}
                                            onChange={e => handleValueChange('hivReceiptPhysician', e)}
                                            isWhiteBackground={true}
                                            isBlankIcon={true}
                                        />
                                    </div>
                                </Fragment>
                            }

                            {((props.formState.hivSubstantiated === "No" && props.formState.hiv === "Yes") || ((state === OREGON || state === MARYLAND) && props.formState.aids === "Yes" && ((props.formState.hivSubstantiated === "No" && state === OREGON) || (state === MARYLAND && props.formState.hivSubstantiated === "Yes" && props.formState.hivSubstantiatedSevenYears === 'No'))) || (state === MAINE && props.formState.aids === "No") || (props.formState.hiv === "Yes" && state === ARIZONA)) && props.formState.hivReceiptPhysician === "Yes" &&
                                <Fragment>
                                    <h6 className='pb-2'>Physician </h6>
                                    <div className='mb-3'>
                                        <TextInput
                                            name="hivReceiptPhysicianName"
                                            id="hivReceiptPhysicianName"
                                            label="Name"
                                            value={props.formState.hivReceiptPhysicianName}
                                            onChange={(e) => handleValueChange('hivReceiptPhysicianName', e.target.value)}
                                        />
                                    </div>
                                    <div className='mb-3'>
                                        <TextInput
                                            name="hivReceiptPhysicianStreet"
                                            id="hivReceiptPhysicianStreet"
                                            label="Street Address"
                                            value={props.formState.hivReceiptPhysicianStreet}
                                            onChange={(e) => handleValueChange('hivReceiptPhysicianStreet', e.target.value)}
                                        />
                                    </div>
                                    <div className='mb-3'>
                                        <TextInput
                                            name="hivReceiptPhysicianCity"
                                            id="hivReceiptPhysicianCity"
                                            label="City"
                                            value={props.formState.hivReceiptPhysicianCity}
                                            onChange={(e) => handleValueChange('hivReceiptPhysicianCity', e.target.value)}
                                        />
                                    </div>
                                    <div className='mb-3'>
                                        <SelectInput
                                            name="hivReceiptPhysicianState"
                                            id="hivReceiptPhysicianState"
                                            value={props.formState.hivReceiptPhysicianState}
                                            onChange={e => handleValueChange('hivReceiptPhysicianState', e.target.value)}
                                            label="State"
                                            selectOptions={GetStateListForAflac()}
                                        />
                                    </div>
                                    <div className='mb-3'>
                                        <TextInput
                                            name="hivReceiptPhysicianZip"
                                            id="hivReceiptPhysicianZip"
                                            label="Zip"
                                            value={props.formState.hivReceiptPhysicianZip}
                                            onChange={(e) => handleValueChange('hivReceiptPhysicianZip', e.target.value)}
                                        />
                                    </div>
                                </Fragment>
                            }
                        </Col>
                    </Row>
                    <div className='backnext-wrapper'>
                        <BackNext onBack={props.onBack} isAflac={true} continueLabel={{ text: "Next" }} />
                    </div>
                </form>
            </FormProvider>
        </Container>
    );
}

HIVEligibility.propTypes = {
    formState: PropTypes.object.isRequired,
    onNext: PropTypes.func,
    onFieldChange: PropTypes.func.isRequired,
    onBack: PropTypes.func,
};

HIVEligibility.ValidationSchema = object().shape({
    hiv: string().when('state', {
        is: (state) => state === ARIZONA || state === COLORADO,
        then: (schema) => schema.required("This field is required"),
        otherwise: (schema) => schema.notRequired()
    }),
    aids: string().when('state', {
        is: (state) => state === MAINE || state === MARYLAND || state === OREGON,
        then: (schema) => schema.required('This field is required'),
        otherwise: (schema) => schema.notRequired()
    }),
    hivPositive: string().when(
        ['state', 'hiv'],
        ([state, hiv], schema) => {
            if (state === ARIZONA && hiv === "Yes") {
                return schema.required('This field is required');
            }
            return schema.notRequired();
        }
    ),
    hivSubstantiated: string().when(
        ['hiv', "state", "aids"],
        ([hiv, state, aids], schema) => {
            if ((hiv === "Yes" && state === COLORADO) || ((state === MARYLAND || state === OREGON) && aids === "Yes")) {
                return schema.required('This field is required.');
            }
            return schema.notRequired();
        }
    ),
    hivPositiveMultispot: string().when(
        ['state', 'hivSubstantiated'],
        ([state, hivSubstantiated], schema) => {
            if (state === COLORADO && hivSubstantiated === "No") {
                return schema.required('This field is required');
            }
            return schema.notRequired();
        }
    ),
    hivSubstantiatedSevenYears: string().when(
        ['state', 'hivSubstantiated'],
        ([state, hivSubstantiated], schema) => {
            if (state === MARYLAND && hivSubstantiated === "Yes") {
                return schema.required('This field is required');
            }
            return schema.notRequired();
        }
    ),
    hivReceiptPhysician: string().when(
        ['hivSubstantiated', "state", "aids", "hiv", "hivSubstantiatedSevenYears", "hivPositiveMultispot", 'hivPositive'],
        ([hivSubstantiated, state, aids, hiv, hivSubstantiatedSevenYears, hivPositiveMultispot, hivPositive], schema) => {
            if ((hiv === "Yes" && state === ARIZONA && hivPositive) || ((hivSubstantiated === "No" && state !== COLORADO) && hiv === "Yes") || ((state === OREGON || state === MARYLAND) && aids === "Yes" && ((hivSubstantiated === "No" && state === OREGON) || (state === MARYLAND && hivSubstantiated === "Yes" && hivSubstantiatedSevenYears === 'No'))) || (state === MAINE && aids === "No") || (state === COLORADO && hivSubstantiated === 'No' && hivPositiveMultispot === 'No')) {
                return schema.required('This field is required');
            }
            return schema.notRequired();
        }
    ),
    hivTestsPerformed: string().when(
        ["hivSubstantiated", "hiv", 'hivPositiveMultispot', 'state', 'aids', 'hivSubstantiatedSevenYears', 'hivPositive'],
        ([hivSubstantiated, hiv, hivPositiveMultispot, state, aids, hivSubstantiatedSevenYears, hivPositive], schema) => {
            if ((hiv === "Yes" && state === ARIZONA && hivPositive === "Yes") || (hiv === "Yes" && ((hivSubstantiated === "Yes" && state !== COLORADO) || (hivSubstantiated === "No" && hivPositiveMultispot === "Yes" && state === COLORADO))) || (aids === "Yes" && (state === MARYLAND && hivSubstantiated === "Yes" && hivSubstantiatedSevenYears === "Yes") || (state === OREGON && hivSubstantiated === "Yes"))) {
                return customValidators.string().isValidNameWithFiftyChar('This field is required', true)
            }
            return schema.notRequired();
        }
    ),
    hivTestsDate: mixed().when(
        ["hivSubstantiated", "hiv", 'hivPositiveMultispot', 'state', 'aids', 'hivSubstantiatedSevenYears', 'hivPositive'],
        ([hivSubstantiated, hiv, hivPositiveMultispot, state, aids, hivSubstantiatedSevenYears, hivPositive], schema) => {
            if ((hiv === "Yes" && state === ARIZONA && hivPositive === "Yes") || (hiv === "Yes" && ((hivSubstantiated === "Yes" && state !== COLORADO) || (hivSubstantiated === "No" && hivPositiveMultispot === "Yes" && state === COLORADO))) || (aids === "Yes" && (state === MARYLAND && hivSubstantiated === "Yes" && hivSubstantiatedSevenYears === "Yes") || (state === OREGON && hivSubstantiated === "Yes"))) {
                return mixed().nullable()
                    .required("Date is required")
                    .test({
                        message: "Invalid Date",
                        test: (value) => !value || dayjs(value).isValid()
                    })
                    .typeError("Invalid date")
            }
            return schema.notRequired().nullable();
        }
    ),
    hivPhysicianName: string().when(
        ["hivSubstantiated", "hiv", 'hivPositiveMultispot', 'state', 'aids', 'hivSubstantiatedSevenYears', 'hivPositive'],
        ([hivSubstantiated, hiv, hivPositiveMultispot, state, aids, hivSubstantiatedSevenYears, hivPositive], schema) => {
            if ((hiv === "Yes" && state === ARIZONA && hivPositive === "Yes") || (hiv === "Yes" && ((hivSubstantiated === "Yes" && state !== COLORADO) || (hivSubstantiated === "No" && hivPositiveMultispot === "Yes" && state === COLORADO))) || (aids === "Yes" && (state === MARYLAND && hivSubstantiated === "Yes" && hivSubstantiatedSevenYears === "Yes") || (state === OREGON && hivSubstantiated === "Yes"))) {
                return customValidators.string().isValidNameWithFiftyChar('This field is required', true)
            }
            return schema.notRequired();
        }
    ),
    hivPhysicianCity: string().when(
        ["hivSubstantiated", "hiv", 'hivPositiveMultispot', 'state', 'aids', 'hivSubstantiatedSevenYears', 'hivPositive'],
        ([hivSubstantiated, hiv, hivPositiveMultispot, state, aids, hivSubstantiatedSevenYears, hivPositive], schema) => {
            if ((hiv === "Yes" && state === ARIZONA && hivPositive === "Yes") || (hiv === "Yes" && ((hivSubstantiated === "Yes" && state !== COLORADO) || (hivSubstantiated === "No" && hivPositiveMultispot === "Yes" && state === COLORADO))) || (aids === "Yes" && (state === MARYLAND && hivSubstantiated === "Yes" && hivSubstantiatedSevenYears === "Yes") || (state === OREGON && hivSubstantiated === "Yes"))) {
                return customValidators.string().isValidNameLimitedSpecialChar('City is required.', true, false)
            }
            return schema.notRequired();
        }
    ),
    hivPhysicianStreet: string().when(
        ["hivSubstantiated", "hiv", 'hivPositiveMultispot', 'state', 'aids', 'hivSubstantiatedSevenYears', 'hivPositive'],
        ([hivSubstantiated, hiv, hivPositiveMultispot, state, aids, hivSubstantiatedSevenYears, hivPositive], schema) => {
            if ((hiv === "Yes" && state === ARIZONA && hivPositive === "Yes") || (hiv === "Yes" && ((hivSubstantiated === "Yes" && state !== COLORADO) || (hivSubstantiated === "No" && hivPositiveMultispot === "Yes" && state === COLORADO))) || (aids === "Yes" && (state === MARYLAND && hivSubstantiated === "Yes" && hivSubstantiatedSevenYears === "Yes") || (state === OREGON && hivSubstantiated === "Yes"))) {
                return customValidators.string().isValidNameLimitedSpecialChar('Street is required.', true, false)
            }
            return schema.notRequired();
        }
    ),
    hivPhysicianState: string().when(
        ["hivSubstantiated", "hiv", 'hivPositiveMultispot', 'state', 'aids', 'hivSubstantiatedSevenYears', 'hivPositive'],
        ([hivSubstantiated, hiv, hivPositiveMultispot, state, aids, hivSubstantiatedSevenYears, hivPositive], schema) => {
            if ((hiv === "Yes" && state === ARIZONA && hivPositive === "Yes") || (hiv === "Yes" && ((hivSubstantiated === "Yes" && state !== COLORADO) || (hivSubstantiated === "No" && hivPositiveMultispot === "Yes" && state === COLORADO))) || (aids === "Yes" && (state === MARYLAND && hivSubstantiated === "Yes" && hivSubstantiatedSevenYears === "Yes") || (state === OREGON && hivSubstantiated === "Yes"))) {
                return schema.required("State is required")
            }
            return schema.notRequired();
        }
    ),
    hivPhysicianZip: string().when(
        ["hivSubstantiated", "hiv", 'hivPositiveMultispot', 'state', 'aids', 'hivSubstantiatedSevenYears', 'hivPositive'],
        ([hivSubstantiated, hiv, hivPositiveMultispot, state, aids, hivSubstantiatedSevenYears, hivPositive], schema) => {
            if ((hiv === "Yes" && state === ARIZONA && hivPositive === "Yes") || (hiv === "Yes" && ((hivSubstantiated === "Yes" && state !== COLORADO) || (hivSubstantiated === "No" && hivPositiveMultispot === "Yes" && state === COLORADO))) || (aids === "Yes" && (state === MARYLAND && hivSubstantiated === "Yes" && hivSubstantiatedSevenYears === "Yes") || (state === OREGON && hivSubstantiated === "Yes"))) {
                return schema.required("Zip is required").matches(/^[0-9]{5}$/, 'Zip code must be 5 number characters.');
            }
            return schema.notRequired();
        }
    ),
    hivReceiptPhysicianName: string().when(
        ["hivReceiptPhysician", "hivSubstantiated", "hiv", "state", "aids", "hivSubstantiatedSevenYears"],
        ([hivReceiptPhysician, hivSubstantiated, hiv, state, aids, hivSubstantiatedSevenYears], schema) => {
            if (((hiv === "Yes" && state === ARIZONA) || (hivSubstantiated === "No" && hiv === "Yes") || ((state === OREGON || state === MARYLAND) && aids === "Yes" && ((hivSubstantiated === "No" && state === OREGON) || (state === MARYLAND && hivSubstantiated === "Yes" && hivSubstantiatedSevenYears === 'No'))) || (state === MAINE && aids === "No")) && hivReceiptPhysician === "Yes") {
                return customValidators.string().isValidNameWithFiftyChar('Name is required.', true)
            }
            return schema.notRequired();
        }
    ),
    hivReceiptPhysicianStreet: string().when(
        ["hivReceiptPhysician", "hivSubstantiated", "hiv", "state", "aids", "hivSubstantiatedSevenYears"],
        ([hivReceiptPhysician, hivSubstantiated, hiv, state, aids, hivSubstantiatedSevenYears], schema) => {
            if (((hiv === "Yes" && state === ARIZONA) || (hivSubstantiated === "No" && hiv === "Yes") || ((state === OREGON || state === MARYLAND) && aids === "Yes" && ((hivSubstantiated === "No" && state === OREGON) || (state === MARYLAND && hivSubstantiated === "Yes" && hivSubstantiatedSevenYears === 'No'))) || (state === MAINE && aids === "No")) && hivReceiptPhysician === "Yes") {
                return customValidators.string().isValidNameLimitedSpecialChar('Street is required.', true, true)
            }
            return schema.notRequired();
        }
    ),
    hivReceiptPhysicianCity: string().when(
        ["hivReceiptPhysician", "hivSubstantiated", "hiv", "state", "aids", "hivSubstantiatedSevenYears"],
        ([hivReceiptPhysician, hivSubstantiated, hiv, state, aids, hivSubstantiatedSevenYears], schema) => {
            if (((hiv === "Yes" && state === ARIZONA) || (hivSubstantiated === "No" && hiv === "Yes") || ((state === OREGON || state === MARYLAND) && aids === "Yes" && ((hivSubstantiated === "No" && state === OREGON) || (state === MARYLAND && hivSubstantiated === "Yes" && hivSubstantiatedSevenYears === 'No'))) || (state === MAINE && aids === "No")) && hivReceiptPhysician === "Yes") {
                return string().isValidNameLimitedSpecialChar('City is required.', true, false)
            }
            return schema.notRequired();
        }
    ),
    hivReceiptPhysicianState: string().when(
        ["hivReceiptPhysician", "hivSubstantiated", "hiv", "state", "aids", "hivSubstantiatedSevenYears"],
        ([hivReceiptPhysician, hivSubstantiated, hiv, state, aids, hivSubstantiatedSevenYears], schema) => {
            if (((hiv === "Yes" && state === ARIZONA) || (hivSubstantiated === "No" && hiv === "Yes") || ((state === OREGON || state === MARYLAND) && aids === "Yes" && ((hivSubstantiated === "No" && state === OREGON) || (state === MARYLAND && hivSubstantiated === "Yes" && hivSubstantiatedSevenYears === 'No'))) || (state === MAINE && aids === "No")) && hivReceiptPhysician === "Yes") {
                return schema.required("State is required")
            }
            return schema.notRequired();
        }
    ),
    hivReceiptPhysicianZip: string().when(
        ["hivReceiptPhysician", "hivSubstantiated", "hiv", "state", "aids", "hivSubstantiatedSevenYears"],
        ([hivReceiptPhysician, hivSubstantiated, hiv, state, aids, hivSubstantiatedSevenYears], schema) => {
            if (((hiv === "Yes" && state === ARIZONA) || (hivSubstantiated === "No" && hiv === "Yes") || ((state === OREGON || state === MARYLAND) && aids === "Yes" && ((hivSubstantiated === "No" && state === OREGON) || (state === MARYLAND && hivSubstantiated === "Yes" && hivSubstantiatedSevenYears === 'No'))) || (state === MAINE && aids === "No")) && hivReceiptPhysician === "Yes") {
                return schema.required("Zip is required").matches(/^[0-9]{5}$/, 'Zip code must be 5 number characters.');
            }
            return schema.notRequired();
        }
    ),
    ineligible: mixed().when(
        ['hiv', 'state', 'aids'],
        ([hiv, state, aids], schema) => {
            if ((hiv === 'Yes' && state === COLORADO) || (aids === 'Yes' && state === MAINE)) {
                return yuparray(object().shape({
                    name: yuparray().of(string().required('Name is required.'))
                        .min(1, 'Name is required.'),
                    numberOfChildren: string().when('name', {
                        is: (name) => name && name.includes('Dependent Child'),
                        then: (schema) => schema.required('This filed is required.'),
                        otherwise: (schema) => schema.notRequired(),
                    }),
                    nameofChildren: yuparray().when('name', {
                        is: (name) => name && name.includes('Dependent Child'),
                        then: (schema) => yuparray(
                            object().shape({
                                name: customValidators.string().isValidNameWithFiftyChar('Child Name is required.', true),
                            })
                        ),
                    }),
                }))
            }
            return schema.notRequired();
        }
    ),
});

HIVEligibility.DefaultFormState = (prefill = false) => {
    return {
        hiv: prefill ? '' : '',
        aids: prefill ? '' : '',
        hivPositive: prefill ? '' : '',
        hivSubstantiatedSevenYears: prefill ? '' : '',
        hivPositiveMultispot: prefill ? '' : '',
        hivSubstantiated: prefill ? "" : '',
        hivReceiptPhysician: prefill ? "" : '',
        ineligibleForCoverage: '',
        hivTestsPerformed: '',
        hivPhysicianName: '',
        hivPhysicianState: '',
        hivPhysicianCity: '',
        hivPhysicianStreet: '',
        hivPhysicianZip: '',
        hivReceiptPhysicianCity: '',
        hivReceiptPhysicianName: '',
        hivReceiptPhysicianState: '',
        hivReceiptPhysicianStreet: '',
        hivReceiptPhysicianZip: '',
        ineligible: [],
        hivTestsDate: null,
        anotherChild: "",
        isVisible: false
    };
};
